import { jsx as _jsx } from "react/jsx-runtime";
import { inputBaseClasses, formLabelClasses, TextField, useTheme, inputAdornmentClasses, selectClasses, autocompleteClasses, } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
const debugLabelPlaceholder = false;
const debugValue = false;
const debugInput = false;
export const AutosizedTextField = (props) => {
    const { label, placeholder, onChange, ...rest } = props;
    const value = props.value ?? props.inputProps?.value ?? props.defaultValue ?? props.inputProps?.defaultValue;
    const theme = useTheme();
    const ref = useRef(null);
    const [paddingLeft, setPaddingLeft] = useState(undefined);
    const [paddingRight, setPaddingRight] = useState(undefined);
    const [fontSize, setFontSize] = useState(undefined);
    useEffect(() => {
        if (ref.current) {
            const input = ref.current.querySelector(`div.${inputBaseClasses.root} > input`);
            if (input === null)
                return;
            if (input.computedStyleMap && typeof input.computedStyleMap === 'function') {
                const computedStyles = input.computedStyleMap();
                const paddingLeft = computedStyles.get('padding-left');
                const paddingRight = computedStyles.get('padding-right');
                const fontSize = computedStyles.get('font-size');
                setPaddingLeft(paddingLeft?.value); // In extremely rare cases, this can somehow not be set. The only example I have is the subtype dropdown on the area portal label
                setPaddingRight(paddingRight?.value);
                setFontSize(fontSize?.value);
            }
            else {
                /**
                 * FireFox doesn't support `computedStyleMap()` — https://developer.mozilla.org/en-US/docs/Web/API/Element/computedStyleMap
                 * So we must find a workaround with `getComputedStyle()` instead — https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle
                 * Or at least, don't crash!
                 */
                const computedStyles = getComputedStyle(input);
                // we assume the results are in pixels (`px`) and strip all non-numeric characters, then convert to a number
                const paddingLeft = parseFloat(computedStyles.paddingLeft.replaceAll(/\D/g, ''));
                const paddingRight = parseFloat(computedStyles.paddingRight.replaceAll(/\D/g, ''));
                const fontSize = parseFloat(computedStyles.fontSize.replaceAll(/\D/g, ''));
                setPaddingLeft(paddingLeft);
                setPaddingRight(paddingRight);
                setFontSize(fontSize);
            }
        }
    }, [ref]);
    const colour = Object.entries(theme.palette)
        .filter(([name]) => name === rest.color)
        .pop()?.[1] ?? undefined;
    return (_jsx(TextField, { ref: ref, value: value, onChange: onChange, label: label, placeholder: placeholder, size: "small", ...rest, sx: {
            // wrap the input value width
            // https://css-tricks.com/auto-growing-inputs-textareas/#aa-other-ideas
            [`& .${inputBaseClasses.root}`]: {
                display: 'inline-grid',
                verticalAlign: 'top',
                alignItems: 'center',
                position: 'relative',
                color: colour?.main,
                '&::before': {
                    ...rest.inputProps?.style,
                    content: `'${(placeholder ?? label ?? '').toString().replaceAll('\\', '\\\\').replaceAll("'", "\\'")}'`,
                    visibility: !debugLabelPlaceholder ? 'hidden' : 'visible',
                    overflow: !debugLabelPlaceholder ? 'hidden' : 'visible',
                    backgroundColor: !debugLabelPlaceholder ? undefined : 'red',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    whiteSpace: 'preserve nowrap',
                    textOverflow: 'ellipsis',
                    paddingLeft: paddingLeft && `${paddingLeft}px`,
                    paddingRight: paddingRight && `${paddingRight}px`,
                    width: 'auto',
                    gridRowStart: 1,
                    // @ts-expect-error `startAdornment` does not exist on type...
                    gridColumnStart: props.slotProps?.input?.startAdornment !== undefined ? 2 : 1,
                },
                '&::after': {
                    ...rest.inputProps?.style,
                    content: `'${value?.toString().replaceAll('\\', '\\\\').replaceAll("'", "\\'")}'`,
                    visibility: !debugValue ? 'hidden' : 'visible',
                    overflow: !debugValue ? 'hidden' : 'visible',
                    backgroundColor: !debugValue ? undefined : 'orange',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    whiteSpace: 'preserve nowrap',
                    textOverflow: 'ellipsis',
                    paddingLeft: paddingLeft && `${paddingLeft}px`,
                    paddingRight: paddingRight && `${paddingRight}px`,
                    width: 'auto',
                    gridRowStart: 1,
                    // @ts-expect-error `startAdornment` does not exist on type...
                    gridColumnStart: props.slotProps?.input?.startAdornment !== undefined ? 2 : 1,
                },
            },
            [`& .${formLabelClasses.root}[data-shrink=false]`]: {
                fontSize: fontSize !== undefined && `${fontSize}px`,
            },
            [`& .${inputAdornmentClasses.positionStart}`]: {
                gridArea: '1',
            },
            [`& .${inputAdornmentClasses.positionEnd}`]: {
                gridArea: '1 / 3',
            },
            [`& .${selectClasses.select}`]: {
                gridRowStart: 1,
                // @ts-expect-error `startAdornment` does not exist on type...
                gridColumnStart: props.slotProps?.input?.startAdornment !== undefined ? 2 : 1,
                width: 'unset',
            },
            [`& .${autocompleteClasses.tag}`]: {
            // CRA — 2025 06 disabling: with large amounts of tags, this breaks because it never wraps
            // gridRowStart: 1,
            },
        }, slotProps: {
            ...rest.slotProps,
            htmlInput: {
                ...rest.inputProps,
                size: 2, // don't shrink too small
                style: {
                    ...rest.inputProps?.style,
                    width: 'auto',
                    gridRowStart: 1,
                    // @ts-expect-error `startAdornment` does not exist on type...
                    gridColumnStart: props.slotProps?.input?.startAdornment !== undefined ? 2 : 1,
                    backgroundColor: !debugInput ? undefined : 'yellow',
                },
            },
            inputLabel: {
                shrink: value !== undefined && 0 < value.toString().length, // RSE reversed order as need to override default behavior; don't want label to revert to placeholder when user clears input
                ...rest.slotProps?.inputLabel,
            },
        } }));
};
